import * as React from 'react';
import { Route } from 'react-router-dom';
import { Image, Group, LocalLaundryService, MonetizationOn, ContactSupport, Work } from '@material-ui/icons';
import { Admin, Resource, usePermissions } from 'react-admin';

import Dashboard from './pages/Dashboard';
import Login from './pages/Login'
import Financials from './pages/Financials';

import authProvider from './utils/authProvider';
import dataProvider from './utils/dataProvider'
import theme from './theme';
import AdminLayout from './components/AdminLayout';
import { hasAccess } from './utils/permissions';

import { UserList, UserEdit, UserShow } from './pages/Users';
import { DefaultServicesList, DefaultServicesCreate, DefaultServicesEdit, DefaultServicesShow } from './pages/DefaultServices';
import { UserServicesList, UserServicesShow } from './pages/UserServices';
import { RequestsList, RequestShow } from './pages/Requests';
import { FeedList, FeedShow } from './pages/Feeds';
import { LogsList } from './pages/Logs';
import AuditLogs from './pages/AuditLogs';
import { TicketsList, TicketsCreate, TicketsEdit, TicketsShow } from './pages/Tickets';
import Reports from './pages/Reports';
import DisputeQueue from './pages/DisputeQueue';
import NoShowQueue from './pages/NoShowQueue';
import CancellationConsole from './pages/CancellationConsole';
import PageAnalytics from './pages/PageAnalytics';
import UserJourney from './pages/UserJourney';
import RecentlyActiveUsers from './pages/RecentlyActiveUsers';
import GuestVisitors from './pages/GuestVisitors';
import DebtDashboard from './pages/DebtDashboard';
import VerificationQueue from './pages/VerificationQueue';
import Pricing from './pages/Pricing';
import ContactModerationQueue from './pages/ContactModerationQueue';
import CredentialsReview from './pages/CredentialsReview';
import TestCredits from './pages/TestCredits';
import ReferralRewards from './pages/ReferralRewards';
import Translations from './pages/Translations';
import Communities from './pages/Communities';
import MetaFlags from './pages/MetaFlags';
import FeatureFlags from './pages/FeatureFlags';


/**
 * Wrapper that reads permissions and conditionally renders resources.
 * react-admin v3 renders Resource children of Admin, so we gate them here.
 */
const PermissionedResources = () => {
    const { permissions } = usePermissions();
    const adminRoles = (permissions && permissions.adminRoles) || [];

    return (
        <>
            {hasAccess('defaultservices', adminRoles) && (
                <Resource name="defaultservices" options={{ label: 'Default Services' }} list={DefaultServicesList} show={DefaultServicesShow} edit={DefaultServicesEdit} create={DefaultServicesCreate} />
            )}
            {hasAccess('financials', adminRoles) && (
                <Resource name="financials" icon={MonetizationOn} list={Financials} />
            )}
            {hasAccess('users', adminRoles) && (
                <Resource name="users" icon={Group} list={UserList} show={UserShow} edit={UserEdit} />
            )}
            {hasAccess('tickets', adminRoles) && (
                <Resource icon={ContactSupport} name="tickets" list={TicketsList} show={TicketsShow} edit={TicketsEdit} />
            )}
            {hasAccess('userservices', adminRoles) && (
                <Resource name="userservices" options={{ label: 'Registered Services' }} icon={LocalLaundryService} list={UserServicesList} show={UserServicesShow} />
            )}
            {hasAccess('feeds', adminRoles) && (
                <Resource name="feeds" icon={Image} list={FeedList} show={FeedShow} />
            )}
            {hasAccess('requests', adminRoles) && (
                <Resource name="requests" icon={Work} list={RequestsList} show={RequestShow} />
            )}
            {hasAccess('logs', adminRoles) && (
                <Resource name="logs" list={LogsList} />
            )}
        </>
    );
};

const App = () => (
    <Admin
        dataProvider={dataProvider}
        authProvider={authProvider}
        dashboard={Dashboard}
        loginPage={Login}
        theme={theme}
        layout={AdminLayout}
        customRoutes={[
            <Route key="reports" exact path="/reports" component={Reports} />,
            <Route key="reports-tab" exact path="/reports/:tab" component={Reports} />,
            <Route key="disputes" exact path="/disputes" component={DisputeQueue} />,
            <Route key="noshow" exact path="/noshow-claims" render={() => { window.location.hash = '#/disputes'; return null; }} />,
            <Route key="cancellations" exact path="/cancellations" component={CancellationConsole} />,
            <Route key="page-analytics" exact path="/page-analytics" component={PageAnalytics} />,
            <Route key="user-journey" exact path="/user-journey" component={UserJourney} />,
            <Route key="recently-active-users" exact path="/recently-active-users" component={RecentlyActiveUsers} />,
            <Route key="guest-visitors" exact path="/guest-visitors" component={GuestVisitors} />,
            <Route key="audit-logs" exact path="/audit-logs" component={AuditLogs} />,
            <Route key="debt" exact path="/debt" component={DebtDashboard} />,
            <Route key="verification" exact path="/verification-queue" component={VerificationQueue} />,
            <Route key="pricing" exact path="/pricing" component={Pricing} />,
            <Route key="contact-moderation" exact path="/contact-moderation" component={ContactModerationQueue} />,
            <Route key="credentials-review" exact path="/credentials-review" component={CredentialsReview} />,
            <Route key="test-credits" exact path="/test-credits" component={TestCredits} />,
            <Route key="referral-rewards" exact path="/referral-rewards" component={ReferralRewards} />,
            <Route key="referral-rewards-user" exact path="/referral-rewards/:userId" component={ReferralRewards} />,
            <Route key="translations" exact path="/translations" component={Translations} />,
            <Route key="communities" exact path="/communities" component={Communities} />,
            <Route key="meta-flags" exact path="/meta-flags" component={MetaFlags} />,
            <Route key="feature-flags" exact path="/feature-flags" component={FeatureFlags} />,
        ]}
    >
        {permissions => {
            const adminRoles = (permissions && permissions.adminRoles) || [];
            return [
                hasAccess('defaultservices', adminRoles) && (
                    <Resource key="defaultservices" name="defaultservices" options={{ label: 'Default Services' }} list={DefaultServicesList} show={DefaultServicesShow} edit={DefaultServicesEdit} create={DefaultServicesCreate} />
                ),
                hasAccess('financials', adminRoles) && (
                    <Resource key="financials" name="financials" icon={MonetizationOn} list={Financials} />
                ),
                hasAccess('users', adminRoles) && (
                    <Resource key="users" name="users" icon={Group} list={UserList} show={UserShow} edit={UserEdit} />
                ),
                hasAccess('tickets', adminRoles) && (
                    <Resource key="tickets" name="tickets" icon={ContactSupport} list={TicketsList} show={TicketsShow} edit={TicketsEdit} />
                ),
                hasAccess('userservices', adminRoles) && (
                    <Resource key="userservices" name="userservices" options={{ label: 'Registered Services' }} icon={LocalLaundryService} list={UserServicesList} show={UserServicesShow} />
                ),
                hasAccess('feeds', adminRoles) && (
                    <Resource key="feeds" name="feeds" icon={Image} list={FeedList} show={FeedShow} />
                ),
                hasAccess('requests', adminRoles) && (
                    <Resource key="requests" name="requests" icon={Work} list={RequestsList} show={RequestShow} />
                ),
                hasAccess('logs', adminRoles) && (
                    <Resource key="logs" name="logs" list={LogsList} />
                ),
            ];
        }}
    </Admin>
);
export default App;
